// src/pages/clab-biodata-rejected/components/FilterPanel.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { recruiterAPI, biodataAPI } from 'utils/api';

const FilterPanel = ({ filters, onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [vdrTypes, setVdrTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillInput, setSkillInput] = useState(filters.skill || '');

  // Predefined filter options
  const editPermissionOptions = [
    { value: 'granted', label: 'Permission Granted' },
    { value: 'not_granted', label: 'No Permission' },
  ];

  const statusOptions = [
    { value: 'recent', label: 'Recently Rejected (Last 7 days)' },
    { value: 'old', label: 'Older Rejections (>30 days)' },
  ];

  const sortOptions = [
    { value: 'created_at', label: 'Newest First' },
    { value: 'created_at_asc', label: 'Oldest First' },
    { value: 'full_name_asc', label: 'Name A-Z' },
    { value: 'full_name_desc', label: 'Name Z-A' },
    { value: 'nationality', label: 'By Nationality' },
  ];

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        
        // Load recruiters
        const recruitersResult = await recruiterAPI.getAllRecruiters();
        if (recruitersResult.success && recruitersResult.data) {
          // Handle different response formats
          const recruitersArray = Array.isArray(recruitersResult.data)
            ? recruitersResult.data
            : recruitersResult.data.data || [];

          setRecruiters(recruitersArray.sort((a, b) => {
            const nameA = a.agency_name || a.name || '';
            const nameB = b.agency_name || b.name || '';
            return nameA.localeCompare(nameB);
          }));
        }

        // Load rejection data to extract filter options
        const rejectedData = await biodataAPI.getBiodataByStatus('rejected', { per_page: 1000 });
        if (rejectedData.success && rejectedData.data) {
          const biodata = rejectedData.data.data || [];
          
          // Extract unique nationalities
          const uniqueNationalities = [...new Set(biodata.map(b => b.nationality).filter(Boolean))];
          setNationalities(uniqueNationalities.sort());
          
          // Extract unique rejection reasons
          const uniqueReasons = [...new Set(
            biodata
              .map(b => b.latest_rejection?.rejection_reason)
              .filter(Boolean)
          )];
          setRejectionReasons(uniqueReasons.sort());
          
          // Extract unique VDR types
          const uniqueVdrTypes = [...new Set(biodata.map(b => b.vdr_type).filter(Boolean))];
          setVdrTypes(uniqueVdrTypes.sort());
        }
      } catch (error) {
        console.error('Failed to load filter options:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  useEffect(() => {
    setSkillInput(filters.skill || '');
  }, [filters.skill]);

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      nationality: '',
      recruiter_id: '',
      date_from: '',
      date_to: '',
      rejection_reason: '',
      vdr_type: '',
      edit_permission_status: '',
      status_filter: '',
      skill: '',
      sort_by: 'created_at'
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== 'created_at');

  const applyQuickFilter = (type) => {
    const today = new Date();
    const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    switch (type) {
      case 'recent':
        handleFilterChange('date_from', oneWeekAgo.toISOString().split('T')[0]);
        handleFilterChange('date_to', today.toISOString().split('T')[0]);
        break;
      case 'month':
        handleFilterChange('date_from', oneMonthAgo.toISOString().split('T')[0]);
        handleFilterChange('date_to', today.toISOString().split('T')[0]);
        break;
      case 'granted_permission':
        handleFilterChange('edit_permission_status', 'granted');
        break;
      case 'no_permission':
        handleFilterChange('edit_permission_status', 'not_granted');
        break;
      default:
        break;
    }
  };

  return (
    <div className="card">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Filter" size={20} className="text-secondary-600" />
            <h3 className="text-lg font-semibold text-text-primary">Advanced Filters</h3>
            {hasActiveFilters && (
              <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                {Object.values(filters).filter(v => v !== '' && v !== 'created_at').length} Active
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-secondary-600 hover:text-secondary-800 font-medium bg-secondary-50 rounded-md transition-colors duration-150"
              >
                <Icon name="X" size={14} />
                <span>Clear All</span>
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
              title={isExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </button>
          </div>
        </div>

        {/* Quick Search - Always Visible */}
        <div className="mt-4 space-y-3">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
            <input
              type="text"
              placeholder="Search by name, passport, worker ID, or remarks..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Quick Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => applyQuickFilter('recent')}
              className="px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full hover:bg-accent-200 transition-colors duration-150"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => applyQuickFilter('month')}
              className="px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full hover:bg-accent-200 transition-colors duration-150"
            >
              Last 30 Days
            </button>
            <button
              onClick={() => applyQuickFilter('granted_permission')}
              className="px-3 py-1 text-xs bg-success-100 text-success-700 rounded-full hover:bg-success-200 transition-colors duration-150"
            >
              Edit Permission Granted
            </button>
            <button
              onClick={() => applyQuickFilter('no_permission')}
              className="px-3 py-1 text-xs bg-warning-100 text-warning-700 rounded-full hover:bg-warning-200 transition-colors duration-150"
            >
              No Edit Permission
            </button>
          </div>
        </div>

        {/* Advanced Filters - Collapsible */}
        {isExpanded && (
          <div className="mt-6 pt-4 border-t border-secondary-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Nationality
                </label>
                <select
                  value={filters.nationality}
                  onChange={(e) => handleFilterChange('nationality', e.target.value)}
                  className="input-field w-full"
                  disabled={loading}
                >
                  <option value="">All Nationalities</option>
                  {nationalities.map((nationality) => (
                    <option key={nationality} value={nationality}>
                      {nationality}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Recruiter Agency
                </label>
                <select
                  value={filters.recruiter_id}
                  onChange={(e) => handleFilterChange('recruiter_id', e.target.value)}
                  className="input-field w-full"
                  disabled={loading}
                >
                  <option value="">All Recruiters</option>
                  {recruiters.map((recruiter) => (
                    <option key={recruiter.id} value={recruiter.id}>
                      {recruiter.agency_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  VDR Type
                </label>
                <select
                  value={filters.vdr_type}
                  onChange={(e) => handleFilterChange('vdr_type', e.target.value)}
                  className="input-field w-full"
                  disabled={loading}
                >
                  <option value="">All VDR Types</option>
                  {vdrTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Rejection Reason
                </label>
                <select
                  value={filters.rejection_reason}
                  onChange={(e) => handleFilterChange('rejection_reason', e.target.value)}
                  className="input-field w-full"
                  disabled={loading}
                >
                  <option value="">All Reasons</option>
                  {rejectionReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Edit Permission
                </label>
                <select
                  value={filters.edit_permission_status}
                  onChange={(e) => handleFilterChange('edit_permission_status', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">All Statuses</option>
                  {editPermissionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Sort By
                </label>
                <select
                  value={filters.sort_by || 'created_at'}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="input-field w-full"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Rejected From
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Rejected To
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Skill keyword
                </label>
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => {
                    setSkillInput(e.target.value);
                    handleFilterChange('skill', e.target.value);
                  }}
                  placeholder="e.g. welding, scaffolding"
                  className="input-field w-full"
                />
              </div>
            </div>

            {/* Filter Summary */}
            {hasActiveFilters && (
              <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="Info" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-primary-800">
                    Active Filters: {Object.entries(filters).filter(([key, value]) => value !== '' && value !== 'created_at').length} applied
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            <span className="ml-2 text-sm text-secondary-600">Loading filter options...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;
